import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StatCard from "../../components/cards/StatCard";
import rankingService from "../../services/rankingService";
import { getErrorMessage } from "../../utils/errorHandler";
import { Briefcase, Users, FileText, Award, BarChart2, ShieldAlert, ArrowRight, BrainCircuit, ExternalLink } from "lucide-react";
import "../../styles/dashboard.css";

const AIDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ stats: {}, offers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await rankingService.getDashboardData();
        setData(res);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getBadgeStyle = (status) => {
    switch (status) {
      case "recommended":
        return { bg: "#ecfdf5", text: "#059669", label: "Recommandé" };
      case "interesting":
        return { bg: "#fffbeb", text: "#d97706", label: "Intéressant" };
      case "review":
      default:
        return { bg: "#fef2f2", text: "#dc2626", label: "A revoir" };
    }
  };

  if (loading) {
    return (
      <div className="flex-center-all" style={{ height: "100vh", background: "#f8fafc", flexDirection: "column" }}>
        <div className="spinner" style={{ width: "40px", height: "40px", border: "4px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%" }}></div>
        <p style={{ marginTop: "15px", color: "#64748b" }}>Chargement du dashboard IA...</p>
      </div>
    );
  }

  const { stats, offers } = data;

  return (
    <div style={{ padding: "24px" }}>
      <div className="dashboard-welcome-header" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "20px", marginBottom: "25px" }}>
        <div>
          <h2 className="welcome-title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <BrainCircuit color="#3b82f6" size={28} />
            Vue Globale du Recrutement IA
          </h2>
          <p className="welcome-subtitle">Supervisez l'intelligence artificielle appliquée à vos offres d'emploi et découvrez les meilleurs talents.</p>
        </div>
        <div>
          <button onClick={() => navigate("/rh/cv-ranking")} className="btn btn-outline" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            Ranking Détaillé
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="form-error-box" style={{ marginBottom: "20px" }}>
          <ShieldAlert size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Statistiques globales */}
      <section className="dashboard-section">
        <div className="stat-cards-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <StatCard
            title="Offres Actives"
            value={`${stats?.active_offers || 0}`}
            icon={Briefcase}
            color="blue"
          />
          <StatCard
            title="Total Candidatures"
            value={`${stats?.total_applications || 0}`}
            icon={Users}
            color="indigo"
          />
          <StatCard
            title="CV Analysés (IA)"
            value={`${stats?.total_analyzed || 0}`}
            icon={FileText}
            color="purple"
          />
          <StatCard
            title="Recommandés"
            value={`${stats?.recommended_candidates || 0}`}
            icon={Award}
            color="green"
          />
          <StatCard
            title="Score Moyen"
            value={`${stats?.average_score || 0}%`}
            icon={BarChart2}
            color="yellow"
          />
        </div>
      </section>

      {/* Grille des offres */}
      <section className="dashboard-section" style={{ marginTop: "30px" }}>
        <h3 style={{ fontSize: "1.25rem", color: "#1e293b", marginBottom: "20px" }}>Classements par Offre</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "20px" }}>
          
          {offers.map((offer) => (
            <div key={offer.id} style={{ background: "white", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              
              {/* Offer Header */}
              <div style={{ padding: "20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h4 style={{ margin: "0 0 5px 0", fontSize: "1.1rem", color: "#0f172a" }}>{offer.title}</h4>
                    <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", display: "flex", alignItems: "center", gap: "5px" }}>
                      {offer.location} â€¢ {offer.applications} candidatures reè§ues
                    </p>
                  </div>
                  <span style={{ fontSize: "0.75rem", fontWeight: "600", padding: "4px 8px", background: "#e0e7ff", color: "#4338ca", borderRadius: "12px" }}>
                    ACTIF
                  </span>
                </div>
              </div>

              {/* Top Candidates List */}
              <div style={{ padding: "20px" }}>
                {offer.top_candidates && offer.top_candidates.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    {offer.top_candidates.map((candidate) => {
                      const badge = getBadgeStyle(candidate.status);
                      return (
                        <div key={candidate.application_id} style={{ display: "flex", flexDirection: "column", gap: "10px", paddingBottom: "15px", borderBottom: "1px dashed #e2e8f0" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ fontWeight: "bold", color: "#64748b", width: "25px" }}>#{candidate.rank}</span>
                                <span style={{ fontWeight: "500", color: "#1e293b" }}>{candidate.candidate}</span>
                              </div>
                              <span style={{ fontSize: "0.75rem", fontWeight: "600", padding: "4px 8px", background: badge.bg, color: badge.text, borderRadius: "12px" }}>
                                {badge.label}
                              </span>
                            </div>
                            
                            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                              <div style={{ flex: 1, background: "#f1f5f9", height: "8px", borderRadius: "4px", overflow: "hidden" }}>
                                <div style={{ width: `${candidate.score}%`, height: "100%", background: badge.text, borderRadius: "4px" }}></div>
                              </div>
                              <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "#475569", width: "40px", textAlign: "right" }}>
                                {candidate.score}%
                              </span>
                              <button 
                                onClick={() => navigate(`/rh/cv-ranking`)}
                                style={{ background: "none", border: "none", color: "#3b82f6", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "0.8rem", fontWeight: "500" }}
                              >
                                Voir
                                <ExternalLink size={14} />
                              </button>
                            </div>
                          </div>
                        );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "#94a3b8", fontSize: "0.9rem" }}>
                    Aucun candidat analysé pour le moment.
                  </div>
                )}
                
                <div style={{ marginTop: "20px", textAlign: "center" }}>
                  <button 
                    onClick={() => navigate("/rh/cv-ranking")}
                    className="btn btn-outline" 
                    style={{ width: "100%", padding: "8px" }}
                  >
                    Voir tous les candidats
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {offers.length === 0 && (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "40px", background: "white", borderRadius: "12px", border: "1px dashed #cbd5e1" }}>
              <Briefcase size={40} color="#94a3b8" style={{ margin: "0 auto 15px auto" }} />
              <p style={{ color: "#64748b", margin: 0 }}>Aucune offre active pour le moment.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AIDashboard;

